/**
 * Sesi back office untuk tab ini.
 *
 * Peraturan (mudah, dan itulah sebabnya ia boleh dipercayai):
 *
 * 1. **Log masuk → sesi disimpan** dalam `sessionStorage` (bukan `localStorage`), jadi menekan F5
 *    tidak memaksa pemilik menaip e-mel dan kata laluan semula.
 * 2. **Keluar dari back office → sesi dipadam.** Menekan "Kembali ke POS", "Kunci", "Log keluar",
 *    atau membuka admin DARIPADA POS semuanya memadam sesi. Jadi kasir yang menekan "Admin" pada PC
 *    kaunter tidak pernah mewarisi sesi pemilik.
 * 3. **12 jam had keras**, dan `sessionStorage` mati bersama tab — PC yang dimatikan tidak
 *    meninggalkan sesi terbuka.
 *
 * E-mel terakhir disimpan berasingan (localStorage) hanya untuk mengisi semula borang. Ia bukan
 * rahsia dan tidak boleh digunakan untuk masuk tanpa kata laluan.
 */

const KEY = '72on-admin-session';
const EMAIL_KEY = '72on-admin-last-email';
const SLUG_KEY = '72on-admin-last-slug';

/** Had keras sesi. Selepas ini, log masuk semula diperlukan. */
const MAX_AGE_MS = 12 * 60 * 60 * 1000;

export interface AdminSession {
  token: string;
  email: string;
  slug: string;
  savedAt: number;
}

function storage(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    // Pelayar yang menyekat storan: sesi hanya dalam memori (log masuk setiap muat semula).
    return null;
  }
}

function persistent(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

/** Simpan sesi selepas log masuk berjaya. */
export function saveAdminSession(session: Omit<AdminSession, 'savedAt'>): void {
  const target = storage();
  if (target !== null) {
    target.setItem(KEY, JSON.stringify({ ...session, savedAt: Date.now() }));
  }

  // Untuk mengisi semula borang sahaja — bukan bukti log masuk.
  const memory = persistent();
  if (memory !== null) {
    memory.setItem(EMAIL_KEY, session.email);
    memory.setItem(SLUG_KEY, session.slug);
  }
}

/**
 * Baca sesi. Pulangkan `null` jika tiada, rosak, atau sudah melebihi 12 jam.
 *
 * Sesi yang luput dipadam supaya ia tidak boleh digunakan semula.
 */
export function readAdminSession(): AdminSession | null {
  const target = storage();
  if (target === null) return null;

  const raw = target.getItem(KEY);
  if (raw === null) return null;

  try {
    const parsed = JSON.parse(raw) as Partial<AdminSession>;
    if (
      typeof parsed.token !== 'string' ||
      typeof parsed.email !== 'string' ||
      typeof parsed.slug !== 'string' ||
      typeof parsed.savedAt !== 'number'
    ) {
      target.removeItem(KEY);
      return null;
    }
    if (Date.now() - parsed.savedAt > MAX_AGE_MS) {
      target.removeItem(KEY);
      return null;
    }
    return parsed as AdminSession;
  } catch {
    target.removeItem(KEY);
    return null;
  }
}

/** Padam sesi: log keluar, kunci, kembali ke POS, atau 401. */
export function clearAdminSession(): void {
  const target = storage();
  if (target === null) return;
  target.removeItem(KEY);
}

/** E-mel dan pautan kedai terakhir, untuk mengisi semula borang log masuk. */
export function readLastIdentity(): { email: string; slug: string } {
  const memory = persistent();
  return {
    email: memory?.getItem(EMAIL_KEY) ?? '',
    slug: memory?.getItem(SLUG_KEY) ?? '',
  };
}
